import { useMemo, memo, useCallback } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
  IMaterialTableProps,
} from '@uniqkey-frontend/shared-app';
import {
  GroupOrganizationsForAdminResponseModel,
} from '@uniqkey-backend-partner/api-client';
import { useNavigate } from 'react-router-dom';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import {
  ORGANIZATION_ACCESS_STATUS_TOOLTIP_TRANSLATION_KEYS,
  ORGANIZATION_ACCESS_STATUS_COLORS,
  ORGANIZATION_ACCESS_STATUS_TRANSLATION_KEYS,
} from '../../../constants';
import { TGetGroupOrganizationsForAdminMethod } from '../../../hooks/useGroupsAPI/interfaces';

export interface IGroupOrganizationsForAdminTableRow extends
  GroupOrganizationsForAdminResponseModel {}

interface IGroupOrganizationsForAdminTableProps extends
  Omit<IUseTableReturn<IGroupOrganizationsForAdminTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetGroupOrganizationsForAdminMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  groupOrganizationsForAdmin: IUseQueryTableFetchReturn<
    TGetGroupOrganizationsForAdminMethod
  >['data'];
  selectedGroupOrganizationsForAdmin: IUseTableReturn<
    IGroupOrganizationsForAdminTableRow
  >['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  groupId: string;
}

const TABLE_OPTIONS: IMaterialTableOptions<IGroupOrganizationsForAdminTableRow> = {
  selection: true,
};

const GroupOrganizationsForAdminTable = (props: IGroupOrganizationsForAdminTableProps) => {
  const {
    groupOrganizationsForAdmin,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedGroupOrganizationsForAdmin,
    onRowSelectionChange,
    createColumns,
    groupId,
  } = props;

  const { t } = useTranslations();
  const navigate = useNavigate();

  const columns = useMemo(() => createColumns([
    {
      title: t('groupPage.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '65%',
    },
    {
      title: t('groupPage.vat'),
      field: 'vat',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.vat}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('groupPage.status'),
      field: 'organizationAccessStatus',
      render: (rowData) => (
        <TypographyWithTooltip
          variant="body2"
          color={ORGANIZATION_ACCESS_STATUS_COLORS[rowData.organizationAccessStatus]}
          title={t(
            ORGANIZATION_ACCESS_STATUS_TOOLTIP_TRANSLATION_KEYS[rowData.organizationAccessStatus],
          )}
        >
          {t(ORGANIZATION_ACCESS_STATUS_TRANSLATION_KEYS[rowData.organizationAccessStatus])}
        </TypographyWithTooltip>
      ),
      width: '20%',
      sorting: false,
    },
  ]), [createColumns, t]);

  const handleGroupOrganizationRowClick = useCallback<
    NonNullable<IMaterialTableProps<IGroupOrganizationsForAdminTableRow>['onRowClick']>
  >((event, organization) => {
    const { organizationId } = organization!;
    navigate(`${PageRouteEnum.Groups}/${groupId}/${organizationId}`);
  }, [groupId, navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={groupOrganizationsForAdmin}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedGroupOrganizationsForAdmin}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleGroupOrganizationRowClick}
      rowSelectByKey="organizationId"
      rowHeight={51}
    />
  );
};

export default memo(GroupOrganizationsForAdminTable);
